import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { localeTag } from "@/lib/i18nFormat";
import { PageHeader, LoadingBlock, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CalendarRange, ChevronLeft, ChevronRight } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const TYPE_STYLE: Record<string, string> = {
  shift: "bg-brand/15 text-brand border-brand/30",
  closure: "bg-status-critical/15 text-status-critical border-status-critical/30",
  store_hours: "bg-status-info/15 text-status-info border-status-info/30",
  task: "bg-status-warn/15 text-status-warn border-status-warn/30",
  training: "bg-purple-500/15 text-purple-500 border-purple-500/30",
  action: "bg-orange-500/15 text-orange-500 border-orange-500/30",
  visit: "bg-teal-500/15 text-teal-500 border-teal-500/30",
  required_read: "bg-pink-500/15 text-pink-500 border-pink-500/30",
};

const monday = (d: Date) => { const x = new Date(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); return x; };
const iso = (d: Date) => d.toISOString().slice(0, 10);

export default function UnifiedCalendar() {
  const {
    t
  } = useI18n();

  const [weekStart, setWeekStart] = useState(monday(new Date()));
  const [data, setData] = useState<any>(null);
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const load = useCallback(() => {
    setData(null);
    const end = new Date(weekStart); end.setDate(end.getDate() + 6);
    api.get(`/ops-calendar?start=${iso(weekStart)}&end=${iso(end)}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [weekStart]);
  useEffect(() => { load(); }, [load]);
  const shift = (n: number) => { const d = new Date(weekStart); d.setDate(d.getDate() + n); setWeekStart(monday(d)); };

  return (
    (<div data-testid="unified-calendar">
      <PageHeader title="Operational Calendar" subtitle="Everything happening across your stores — schedules, hours, tasks, training, visits and deadlines" testId="unified-cal-header" />
      <div className="mb-3 flex items-center gap-2">
        <Button size="icon" variant="outline" onClick={() => shift(-7)} data-testid="cal-prev"><ChevronLeft size={16} /></Button>
        <span className="text-sm font-medium">{days[0].toLocaleDateString(localeTag(), { month: "short", day: "numeric" })} – {days[6].toLocaleDateString(localeTag(), { month: "short", day: "numeric" })}</span>
        <Button size="icon" variant="outline" onClick={() => shift(7)} data-testid="cal-next"><ChevronRight size={16} /></Button>
        <Button size="sm" variant="ghost" onClick={() => setWeekStart(monday(new Date()))} data-testid="cal-today">{t("This week")}</Button>
      </div>
      {!data ? <LoadingBlock /> : data.error ? <EmptyState icon={<CalendarRange size={26} />} title="Unavailable" description="Could not load the calendar." /> : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-7">
          {days.map((d) => {
            const key = iso(d);
            const evs = (data.by_date || {})[key] || [];
            const isToday = key === iso(new Date());
            return (
              <Card key={key} className={`p-2 ${isToday ? "border-brand/50 ring-1 ring-brand/30" : ""}`} data-testid={`cal-day-${key}`}>
                <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">{d.toLocaleDateString(localeTag(), { weekday: "short" })} {d.getDate()}</div>
                {evs.length === 0 ? <div className="py-2 text-[11px] text-muted-foreground">—</div> : (
                  <div className="space-y-1">
                    {evs.map((e: any, i: number) => (
                      <div key={i} className={`rounded border px-1.5 py-1 text-[11px] ${TYPE_STYLE[e.type] || "border-border bg-muted/30"}`} data-testid={`cal-event-${e.type}`}>
                        <span className="font-medium capitalize">{e.type.replace("_", " ")}</span> · {e.title}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
      <div className="mt-3 text-[11px] text-muted-foreground">{t("Only items within your authorized stores and modules are shown.")}</div>
    </div>)
  );
}
